import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import ProjectCard from "./ProjectCard";
import ProjectModal from "./ProjectModal";
import {
  projects,
  categoryConfig,
  getProjectsByCategory,
  getFeaturedProjects,
} from "../data/projects";

const CategoryBlock = ({ category, description, onOpen }) => {
  const [showAll, setShowAll] = useState(false);
  const items = getProjectsByCategory(category);
  const visible = showAll ? items : items.slice(0, 6);
  if (!items.length) return null;

  return (
    <div className="mb-20" data-testid={`homepage-category-${category.replace(/s+/g, "-").toLowerCase()}`}>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
        <div className="max-w-2xl">
          <div className="flex items-center gap-3">
            <h3 className="font-[Fraunces] text-2xl sm:text-3xl font-bold text-[hsl(var(--brand-navy))]">
              {category}
            </h3>
            <span className="text-xs font-semibold rounded-full bg-lilac text-brand-deep px-2.5 py-0.5">
              {items.length} projects
            </span>
          </div>
          <p className="mt-1 text-sm text-slate-500 max-w-xl">{description}</p>
        </div>
        {items.length > 6 && (
          <button
            onClick={() => setShowAll((v) => !v)}
            data-testid={`homepage-toggle-${category.replace(/s+/g, "-").toLowerCase()}`}
            className="text-xs font-semibold text-brand-deep hover:text-brand inline-flex items-center gap-1 self-start md:self-auto"
          >
            {showAll ? "Show less" : `View all ${items.length}`}
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
        {visible.map((p, i) => (
          <ProjectCard key={p.slug} project={p} onOpen={onOpen} index={i} />
        ))}
      </div>
    </div>
  );
};

export const HomePortfolio = () => {
  const [active, setActive] = useState(null);
  const featured = getFeaturedProjects();

  return (
    <section id="portfolio" className="relative py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-lilac px-3.5 py-1 text-xs font-medium text-brand-deep border border-[hsl(var(--brand-violet)/.15)]">
              <Sparkles className="h-3.5 w-3.5" /> Our Projects
            </div>
            <h2 className="mt-4 text-4xl sm:text-5xl font-bold text-[hsl(var(--brand-navy))]">
              {projects.length}+ projects across{" "}
              <span className="text-gradient-brand">{categoryConfig.length} industries</span>
            </h2>
            <p className="mt-4 text-slate-600 text-base sm:text-lg leading-relaxed">
              Explore websites, applications and digital experiences developed across multiple industries - organised category-wise for easy discovery.
            </p>
          </div>
          <Link
            to="/portfolio"
            data-testid="view-full-portfolio-link"
            className="group inline-flex items-center gap-2 rounded-full bg-[hsl(var(--brand-navy))] hover:bg-brand text-white px-5 py-2.5 text-sm font-semibold self-start md:self-auto"
          >
            View Full Portfolio
            <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>

        {/* Featured strip */}
        {featured.length > 0 && (
          <div className="mb-16">
            <div className="flex items-baseline gap-3 mb-6">
              <h3 className="font-[Fraunces] text-2xl sm:text-3xl font-bold text-[hsl(var(--brand-navy))]">Featured Projects</h3>
              <span className="text-xs text-slate-500">Hand-picked highlights</span>
            </div>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {featured.map((p, i) => (
                <ProjectCard key={`f-${p.slug}`} project={p} onOpen={setActive} index={i} />
              ))}
            </div>
          </div>
        )}

        {/* Category blocks */}
        {categoryConfig.map((c) => (
          <CategoryBlock
            key={c.key}
            category={c.key}
            description={c.description}
            onOpen={setActive}
          />
        ))}
      </div>
      <ProjectModal project={active} onClose={() => setActive(null)} />
    </section>
  );
};

export default HomePortfolio;
